import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import cwmMascotWhite from "../../assets/brand/cwm-logo-mascot-white.png";
import { GROUP_PALETTE } from "./taxonomy";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const DEFAULT_CENTER = [-73.98, 40.725]; // Lower Manhattan
const DEFAULT_ZOOM = 12.2;

/**
 * Real Mapbox GL map. Requires REACT_APP_MAPBOX_TOKEN. When the token is
 * missing (dev without key, or CI), the parent falls back to the stylized
 * SVG map so the app still renders cleanly.
 *
 * Each restaurant with lat/lng gets a CWM-branded pin (mascot silhouette
 * inside a teardrop, colored by taxonomy group). Selecting a pin swaps
 * the preview card rendered below the map.
 */
export default function MapboxMap({ restaurants = [], onChat, PreviewCard }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedId, setSelectedId] = useState(() => restaurants[0]?.id ?? null);

  // Filter to restaurants that have coordinates.
  const placed = useMemo(
    () => restaurants.filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lng)),
    [restaurants],
  );

  // Initialize map once.
  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(
      new mapboxgl.AttributionControl({ compact: true, customAttribution: "" }),
      "bottom-right",
    );
    mapRef.current = map;

    // Mapbox caches the container's dimensions at creation. When the map view
    // is toggled on after first render, those dimensions can be stale and
    // tiles render to a 0×0 canvas. Force a resize once the style has loaded
    // (or on the next frame if `load` is missed) so tiles repaint against
    // the actual container size.
    const resize = () => map.resize();
    requestAnimationFrame(resize);
    map.once("load", resize);
    map.once("idle", resize);
    const ro = new ResizeObserver(resize);
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Sync markers to restaurants. Markers don't need the style to be loaded
  // — the Marker API mounts DOM overlays, independent of tile rendering.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear old markers.
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    placed.forEach((r) => {
      const group = inferGroup(r);
      const palette = GROUP_PALETTE[group] || GROUP_PALETTE.Lifestyle;
      const el = buildPinElement({
        color: palette.active,
        selected: r.id === selectedId,
      });
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        setSelectedId(r.id);
        map.flyTo({ center: [r.lng, r.lat], zoom: Math.max(map.getZoom(), 14), speed: 1.1 });
      });
      const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([r.lng, r.lat])
        .addTo(map);
      markersRef.current.push(marker);
    });

    // Fit to bounds if we have more than one point.
    if (placed.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      placed.forEach((r) => bounds.extend([r.lng, r.lat]));
      map.fitBounds(bounds, { padding: 72, maxZoom: 14.5, duration: 600 });
    } else if (placed.length === 1) {
      map.flyTo({ center: [placed[0].lng, placed[0].lat], zoom: 14, duration: 500 });
    }
  }, [placed, selectedId]);

  const selected = placed.find((r) => r.id === selectedId) || placed[0];

  if (!placed.length) {
    return (
      <div className="cwm-map-empty">
        <img src={cwmMascotWhite} alt="" style={{ height: 56, opacity: 0.7 }} />
        <div style={{ fontWeight: 800, fontSize: 16, color: "#fff", marginTop: 10 }}>
          No mapped restaurants yet
        </div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 4, textAlign: "center", maxWidth: 320 }}>
          The filter results don't include any restaurants with location data.
        </div>
      </div>
    );
  }

  return (
    <div className="cwm-map-wrap">
      <div className="cwm-map-canvas cwm-map-canvas-live">
        <div ref={containerRef} className="cwm-map-gl" />
        <div className="cwm-map-legend">
          <div className="cwm-map-legend-dot" style={{ background: "#1E9BFF" }} />
          <span>{placed.length} places on this map</span>
        </div>
      </div>

      {selected && PreviewCard && (
        <PreviewCard
          r={selected}
          accent={(GROUP_PALETTE[inferGroup(selected)] || GROUP_PALETTE.Lifestyle).active}
          onChat={onChat}
        />
      )}
    </div>
  );
}

function buildPinElement({ color, selected }) {
  const wrap = document.createElement("div");
  wrap.className = "cwm-mb-pin" + (selected ? " is-selected" : "");
  wrap.innerHTML = `
    <span class="cwm-mb-pin-dot" style="--pin-color:${color}">
      <img src="${cwmMascotWhite}" alt="" />
    </span>
  `;
  return wrap;
}

function inferGroup(r) {
  const tags = new Set(
    (r.dietary_display_tags || [])
      .concat(r.safe?.map((s) => s.label) || [])
      .map((x) => (x || "").toLowerCase()),
  );
  const list = [...tags];
  if (list.some((t) => /vegan|vegetarian|pescat|plant/.test(t))) return "Diet";
  if (list.some((t) => /halal|kosher|lenten|jain|buddh/.test(t))) return "Religious";
  if (list.some((t) => /gluten|dairy|nut|peanut|shell|sesame|allergen/.test(t))) return "Allergies";
  if (list.some((t) => /low-sodium|low-glyc|fodmap|gerd|ibs|crohn/.test(t))) return "Therapeutic";
  return "Lifestyle";
}

export function mapboxAvailable() {
  return !!MAPBOX_TOKEN;
}
